import { useRef, useState } from 'react';
import { Button, Group, Modal, Stack, Switch, TextInput } from '@mantine/core';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError } from 'src/error/ApiError';
import useSaveTenantSkill from './hooks/useSaveTenantSkill';
import type { SiestaSkill } from './types';

const NAME_PATTERN = /^[a-z][a-z0-9_]{2,49}$/;

type SkillEditorModalProps = {
  opened: boolean;
  skill: SiestaSkill | null;
  onClose: () => void;
};

export default function SkillEditorModal({ opened, skill, onClose }: SkillEditorModalProps) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={skill ? `Edit ${skill.name}` : 'Create a skill'}
      size="xl"
      centered
    >
      {opened && <SkillEditorForm key={skill?.id ?? 'new'} skill={skill} onClose={onClose} />}
    </Modal>
  );
}

function SkillEditorForm({ skill, onClose }: { skill: SiestaSkill | null; onClose: () => void }) {
  const [name, setName] = useState(skill?.name ?? '');
  const [description, setDescription] = useState(skill?.description ?? '');
  const [isEnabled, setIsEnabled] = useState(skill?.isEnabled ?? true);
  const [companyFilesVisible, setCompanyFilesVisible] = useState(
    skill?.companyFilesVisible ?? false,
  );
  // Skills built from this screen always run in the sandbox — that's the technical detail an admin
  // shouldn't have to reason about, so it's forced on rather than surfaced as a toggle.
  const requiresSandbox = true;
  const [hasMarkdownContent, setHasMarkdownContent] = useState(
    (skill?.markdown ?? '').trim().length > 0,
  );
  const markdownRef = useRef(skill?.markdown ?? '');

  const { mutateAsync: saveSkill, isPending } = useSaveTenantSkill();

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const isNameValid = NAME_PATTERN.test(trimmedName);
  const canSave = isNameValid && trimmedDescription.length > 0 && hasMarkdownContent;

  const nameError =
    trimmedName.length > 0 && !isNameValid
      ? 'Use snake_case: lowercase letters, digits and underscores, starting with a letter (3 to 50 characters).'
      : null;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      await saveSkill({
        id: skill?.id,
        name: trimmedName,
        description: trimmedDescription,
        markdown: markdownRef.current,
        requiresSandbox,
        companyFilesVisible,
        isEnabled,
      });
      onClose();
    } catch (error) {
      const detail =
        error instanceof ApiError ? error.message : 'An error occurred while saving the skill.';
      notifyAndLogError('Failed to save skill', detail, error);
    }
  };

  return (
    <Stack gap="md">
      <TextInput
        label="Name"
        description="Identifier Siesta uses to activate the skill (snake_case, e.g. quarterly_review_pack)"
        placeholder="quarterly_review_pack"
        value={name}
        onChange={e => setName(e.currentTarget.value)}
        error={nameError}
        required
      />
      <TextInput
        label="Description"
        description="Short summary Siesta reads to decide when to use the skill"
        placeholder="Build a branded quarterly review pack for a client"
        value={description}
        onChange={e => setDescription(e.currentTarget.value)}
        required
      />
      <Switch
        label="Does Siesta need to see your company documents when using this skill?"
        checked={companyFilesVisible}
        onChange={e => setCompanyFilesVisible(e.currentTarget.checked)}
      />
      <Switch
        label="Enabled"
        description="Disabled skills are hidden from chat without being deleted"
        checked={isEnabled}
        onChange={e => setIsEnabled(e.currentTarget.checked)}
      />
      <MarkdownEditor
        defaultValue={markdownRef.current}
        onChange={value => {
          markdownRef.current = value;
          setHasMarkdownContent(value.trim().length > 0);
        }}
        label="Skill definition"
        rootClass="compact"
      />
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} loading={isPending} disabled={!canSave}>
          Save
        </Button>
      </Group>
    </Stack>
  );
}
